import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { IAsset } from '~/types/common';
import {
  MonitoringMonitoringProtocolClientStatus,
  MonitoringMonitoringProtocolGetMonitoringListRequest,
  PiracyClientModelChannelGroupEnum,
  PiracyCommonEnumsSearchMethodEnum,
  PiracyCommonEnumsSortColumnEnum,
  PiracyCommonEnumsSortOrderEnum,
} from '~/swagger/data-contracts';
import { useGetAllAssetList } from '~/apis/asset';
import { useGetListingCount } from '~/apis/monitoring';
import { AppContext } from '~/components/AppContext';

export interface IMonitoringContext {
  request: MonitoringMonitoringProtocolGetMonitoringListRequest;
  listingCount: number;
  channelGroup: PiracyClientModelChannelGroupEnum;
  setChannelGroup: (group: PiracyClientModelChannelGroupEnum) => void;
  allAssetList: IAsset[];
  status: MonitoringMonitoringProtocolClientStatus;
  setStatus: (status: MonitoringMonitoringProtocolClientStatus) => void;
  search: string | null;
  setSearch: (search: string | null) => void;
  selectedAssetIdList: number[];
  setSelectedAssetIdList: (list: number[]) => void;
  selectedSearchTypeList: PiracyCommonEnumsSearchMethodEnum[];
  setSelectedSearchTypeList: (list: PiracyCommonEnumsSearchMethodEnum[]) => void;
  selectedWebsiteProfileList: number[];
  setSelectedWebsiteProfileList: (list: number[]) => void;
  startDate: string | null;
  setStartDate: (date: string | null) => void;
  endDate: string | null;
  setEndDate: (date: string | null) => void;
  sortColumn: PiracyCommonEnumsSortColumnEnum;
  setSortColumn: (s: PiracyCommonEnumsSortColumnEnum) => void;
  sortOrder: PiracyCommonEnumsSortOrderEnum;
  setSortOrder: (s: PiracyCommonEnumsSortOrderEnum) => void;
  selectedIdList: number[];
  setSelectedIdList: (list: number[]) => void;
  limit: number;
  setLimit: (limit: number) => void;
  offset: number;
  setOffset: (offset: number) => void;
  listingId: number | null;
  setListingId: (id: number | null) => void;
  fixedPanel: 'listing' | null;
  setFixedPanel: (v: 'listing' | null) => void;
  isLoading: boolean;
  handleReset: () => void;
  confirmStatus: boolean;
  canPerformConfirm: (target: MonitoringMonitoringProtocolClientStatus) => boolean;
}

export const MonitoringContext = createContext<IMonitoringContext>({} as IMonitoringContext);

interface IProps {}

const MonitoringContextProvider = ({ children }: React.PropsWithChildren<IProps>) => {
  const { confirmNeeded, googleConfirmNeeded } = useContext(AppContext);
  const [channelGroup, setChannelGroup] = useState<PiracyClientModelChannelGroupEnum>(
    PiracyClientModelChannelGroupEnum.SearchEngine
  );
  const [status, setStatus] = useState<MonitoringMonitoringProtocolClientStatus>(
    MonitoringMonitoringProtocolClientStatus.Detected
  );
  const [search, setSearch] = useState<string | null>(null);
  const [selectedAssetIdList, setSelectedAssetIdList] = useState<number[]>([]);
  const [selectedSearchTypeList, setSelectedSearchTypeList] = useState<PiracyCommonEnumsSearchMethodEnum[]>([]);
  const [selectedWebsiteProfileList, setSelectedWebsiteProfileList] = useState<number[]>([]);
  const [startDate, setStartDate] = useState<string | null>(null);
  const [endDate, setEndDate] = useState<string | null>(null);
  const [sortColumn, setSortColumn] = useState<PiracyCommonEnumsSortColumnEnum>(
    PiracyCommonEnumsSortColumnEnum.DetectedDate
  );
  const [sortOrder, setSortOrder] = useState<PiracyCommonEnumsSortOrderEnum>(PiracyCommonEnumsSortOrderEnum.Desc);
  const [limit, setLimit] = useState<number>(50);
  const [offset, setOffset] = useState<number>(0);
  const [listingId, setListingId] = useState<number | null>(null);
  const [fixedPanel, setFixedPanel] = useState<'listing' | null>(null);
  const [selectedIdList, setSelectedIdList] = useState<number[]>([]);

  const confirmStatus =
    (confirmNeeded && channelGroup !== PiracyClientModelChannelGroupEnum.SearchEngine) ||
    (googleConfirmNeeded && channelGroup === PiracyClientModelChannelGroupEnum.SearchEngine);

  const canPerformConfirm = (target: MonitoringMonitoringProtocolClientStatus) => {
    return confirmStatus && status === target;
  };

  const [request, setRequest] = useState<MonitoringMonitoringProtocolGetMonitoringListRequest>({
    channelGroup: channelGroup,
    clientStatus: status,
    search: search,
    assetList: selectedAssetIdList,
    searchMethodList: selectedSearchTypeList,
    websiteIdList: selectedWebsiteProfileList,
    startDate: startDate,
    endDate: endDate,
    offset: offset,
    limit: limit,
    sortColumn: sortColumn,
    sortOrder: sortOrder,
  });
  const listingCountRequest = {
    assetList: selectedAssetIdList,
    searchMethodList: selectedSearchTypeList,
    websiteIdList: selectedWebsiteProfileList,
    channelGroup: channelGroup,
    clientStatus: status,
    startDate: startDate,
    endDate: endDate,
    search,
  };

  const handleReset = useCallback(() => {
    setSearch(null);
    setSelectedAssetIdList([]);
    setSelectedSearchTypeList([]);
    setSelectedWebsiteProfileList([]);
    setStartDate(null);
    setEndDate(null);
    setOffset(0);
    setLimit(50);
    setSortColumn(PiracyCommonEnumsSortColumnEnum.DetectedDate);
    setSortOrder(PiracyCommonEnumsSortOrderEnum.Desc);
    setFixedPanel(null);
    setSelectedIdList([]);
  }, []);

  useEffect(() => {
    setRequest({
      channelGroup: channelGroup,
      clientStatus: status,
      search: search,
      assetList: selectedAssetIdList,
      searchMethodList: selectedSearchTypeList,
      websiteIdList: selectedWebsiteProfileList,
      startDate: startDate,
      endDate: endDate,
      offset: offset,
      limit: limit,
      sortColumn: sortColumn,
      sortOrder: sortOrder,
    });
  }, [
    search,
    selectedAssetIdList,
    selectedSearchTypeList,
    selectedWebsiteProfileList,
    startDate,
    endDate,
    offset,
    limit,
    sortColumn,
    sortOrder,
    channelGroup,
    status,
  ]);

  useEffect(() => {
    setStatus(MonitoringMonitoringProtocolClientStatus.Detected);
  }, [channelGroup]);

  useEffect(() => {
    handleReset();
  }, [channelGroup, handleReset]);

  const { data: allAssetList = [] } = useGetAllAssetList();

  const { data: countRes } = useGetListingCount(listingCountRequest);
  const listingCount = countRes?.count || 0;

  const isLoading = false;

  return (
    <MonitoringContext.Provider
      value={{
        listingCount,
        request,
        channelGroup,
        setChannelGroup,
        allAssetList,
        status,
        setStatus,
        search,
        setSearch,
        selectedAssetIdList,
        setSelectedAssetIdList,
        selectedSearchTypeList,
        setSelectedSearchTypeList,
        selectedWebsiteProfileList,
        setSelectedWebsiteProfileList,
        startDate,
        setStartDate,
        endDate,
        setEndDate,
        sortColumn,
        setSortColumn,
        sortOrder,
        setSortOrder,
        limit,
        setLimit,
        offset,
        setOffset,
        listingId,
        setListingId,
        fixedPanel,
        setFixedPanel,
        selectedIdList,
        setSelectedIdList,
        isLoading,
        handleReset,
        confirmStatus,
        canPerformConfirm,
      }}
    >
      {children}
    </MonitoringContext.Provider>
  );
};

export default MonitoringContextProvider;
